import React from 'react'

import { Box, SearchField, SortDirection, Table } from '@factorialco/deprecated-design-system'
import orderBy from 'lodash/orderBy'

import { observer } from 'mobx-react'

import createComputed from 'modules/core/hooks/createComputed'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import i18n from 'modules/core/lib/i18n'
import { matchText } from 'modules/core/lib/stringUtils'

import EmptyTableBody from 'modules/finance/components/EmptyTableBody'

import Header from './Header'
import List from './List'
import { ValueMember } from './types'

const sortCostCenterMembers = createComputed(
  (members: ValueMember[], attribute: string, direction: 'asc' | 'desc') =>
    orderBy(
      members,
      (member) => {
        switch (attribute) {
          case 'firstName': {
            return member.employee.firstName.toLocaleLowerCase()
          }
          case 'lastName': {
            return member.employee.lastName.toLocaleLowerCase()
          }
          case 'jobTitle': {
            return (
              member.employee.referenceContract?.jobCatalogLevel?.role?.name ||
              member.employee.referenceContract?.jobTitle
            )
          }
          case 'dedication': {
            return '-'
          }
          default: {
            return attribute
          }
        }
      },
      [direction]
    )
)

const filteredBySearchBox = createComputed((members: ValueMember[], search: string) => {
  return members.filter(
    (target) =>
      matchText(search, target.employee.firstName) ||
      matchText(search, target.employee.lastName) ||
      matchText(
        search,
        target.employee.referenceContract?.jobTitle ||
          target.employee.referenceContract?.jobCatalogLevel?.role?.name ||
          ''
      )
  )
})

interface Props {
  members: ValueMember[]
}
const DimensionValueMembers = ({ members }: Props) => {
  const columns = [{ attribute: 'name' }]
  const hasMembers = members ? members.length > 0 : false

  /*
   * Column sort props
   */
  const [sortDirection, setDirection] = React.useState<SortDirection>('asc')
  const [sortAttribute, setSortAttribute] = React.useState<string>(columns[0]?.attribute ?? '')

  const onSort = (sortAttribute: string, sortDirection: SortDirection) => {
    setDirection(sortDirection)
    setSortAttribute(sortAttribute)
  }

  const sortedMembers = sortCostCenterMembers(members, sortAttribute, sortDirection).get()

  const { search, debouncedSearch, onSearch } = useDebouncedSearch()

  const sortedAndFilteredMembers = filteredBySearchBox(sortedMembers, debouncedSearch).get()

  const tableOptions = () => (
    <Box width="full">
      <SearchField
        variant="big"
        search={search}
        onSearch={onSearch}
        placeholder={i18n.t('common.search')}
      />
    </Box>
  )

  return (
    <Table options={tableOptions} fullWidth>
      <Header onSort={onSort} direction={sortDirection} attribute={sortAttribute} />
      {hasMembers && <List members={sortedAndFilteredMembers} />}
      {!hasMembers && (
        <EmptyTableBody colSpan={9} message={i18n.t('finance.dimension_value_detail.empty')} />
      )}
    </Table>
  )
}

export default observer(DimensionValueMembers)
